import React from 'react';
import { Icon } from './components.jsx';
// L&T IT Helpdesk - Supplier Contacts


  const { useState, useEffect, useCallback, useMemo } = React;

  // True on phone-sized screens - swaps the wide contacts table for a card list.
  function useIsPhone() {
    const q = '(max-width: 600px)';
    const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
    useEffect(() => {
      if (!window.matchMedia) return undefined;
      const mq = window.matchMedia(q);
      const on = () => setPhone(mq.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }, []);
    return phone;
  }

  // ─── Config ───────────────────────────────────────────────────────────────

  const CONTACT_CATS = [
    'Software & Licensing', 'Hardware', 'Cloud & Hosting',
    'Networking', 'Telecoms', 'Security',
    'Maintenance & Support', 'Consultancy', 'Other',
  ];

  const CAT_STYLE = {
    'Software & Licensing':  { color: '#6366F1', bg: '#EEF2FF' },
    'Hardware':              { color: '#F59E0B', bg: '#FFFBEB' },
    'Cloud & Hosting':       { color: '#0EA5E9', bg: '#F0F9FF' },
    'Networking':            { color: '#10B981', bg: '#ECFDF5' },
    'Telecoms':              { color: '#8B5CF6', bg: '#F5F3FF' },
    'Security':              { color: '#EF4444', bg: '#FEF2F2' },
    'Maintenance & Support': { color: '#F97316', bg: '#FFF7ED' },
    'Consultancy':           { color: '#EC4899', bg: '#FDF2F8' },
    'Other':                 { color: '#64748B', bg: '#F1F5F9' },
  };

  const SLA_OPTIONS = ['24/7', 'Business Hours', 'Next Business Day', 'Best Effort', 'Custom'];

  const SLA_STYLE = {
    '24/7':              { color: '#16A34A', bg: '#F0FDF4' },
    'Business Hours':    { color: '#2563EB', bg: '#EFF6FF' },
    'Next Business Day': { color: '#D97706', bg: '#FFFBEB' },
    'Best Effort':       { color: '#64748B', bg: '#F1F5F9' },
    'Custom':            { color: '#7C3AED', bg: '#F5F3FF' },
  };

  // Shared API caller - see window.IT_AUTH.api in index.html.
  const cApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'cApi');

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const expiryInfo = (iso) => {
    if (!iso) return null;
    const days = Math.ceil((new Date(iso) - Date.now()) / 86400000);
    if (days < 0)   return { label: 'Expired',        color: '#EF4444', bg: '#FEF2F2', bold: true };
    if (days <= 30) return { label: `${days}d left`,  color: '#EF4444', bg: '#FEF2F2', bold: true };
    if (days <= 90) return { label: `${days}d left`,  color: '#D97706', bg: '#FFFBEB', bold: true };
    return            { label: fmtDate(iso),           color: '#64748B', bg: null,     bold: false };
  };

  // ─── Badges ───────────────────────────────────────────────────────────────

  const CatBadge = ({ cat }) => {
    const s = CAT_STYLE[cat] || CAT_STYLE['Other'];
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
        color: s.color, background: s.bg, whiteSpace: 'nowrap', display: 'inline-block',
      }}>
        {cat}
      </span>
    );
  };

  const SlaBadge = ({ sla }) => {
    if (!sla || !sla.trim()) return <Dash/>;
    const s = SLA_STYLE[sla] || SLA_STYLE['Custom'];
    return (
      <span style={{
        fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99,
        color: s.color, background: s.bg, border: `1px solid ${s.color}33`,
        whiteSpace: 'nowrap', display: 'inline-block',
      }}>
        {sla}
      </span>
    );
  };

  const ExpiryBadge = ({ expiry }) => {
    if (!expiry) return <Dash/>;
    const info = expiryInfo(expiry);
    if (!info) return <Dash/>;
    return (
      <span style={{
        fontSize: 11, fontWeight: info.bold ? 600 : 400,
        color: info.color,
        background: info.bg || 'transparent',
        padding: info.bg ? '2px 7px' : '0',
        borderRadius: info.bg ? 99 : 0,
        whiteSpace: 'nowrap', display: 'inline-block',
      }}>
        {info.label}
      </span>
    );
  };

  const Dash = () => <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>-</span>;

  // ─── Column header helper ──────────────────────────────────────────────────

  const Th = ({ label, col, sortBy, onSort, style }) => (
    <th
      onClick={() => col && onSort(col)}
      style={{
        padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
        color: sortBy?.col === col ? 'var(--text)' : 'var(--text-subtle)',
        letterSpacing: '0.03em', textTransform: 'uppercase',
        borderBottom: '1px solid var(--line)',
        background: 'var(--bg-sub)',
        cursor: col ? 'pointer' : 'default',
        whiteSpace: 'nowrap', userSelect: 'none',
        position: 'sticky', top: 0, zIndex: 3,
        ...style,
      }}
    >
      {label}
      {col && (
        <span style={{ opacity: sortBy?.col === col ? 1 : 0.3, marginLeft: 4 }}>
          {sortBy?.col === col ? (sortBy.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      )}
    </th>
  );

  // ─── Contact modal (add / view / edit) ────────────────────────────────────

  const EMPTY = {
    company: '', category: 'Other', contactName: '', role: '',
    email: '', phone: '', supportPhone: '', supportEmail: '',
    sla: 'Business Hours', contractExpiry: '', website: '', accountNo: '', notes: '',
  };

  const ContactModal = ({ contact, isNew, onClose, onSave, onDelete }) => {
    const [editing, setEditing] = useState(isNew);
    const [form, setForm]       = useState(contact ? { ...EMPTY, ...contact } : { ...EMPTY });
    const [saving, setSaving]   = useState(false);
    const isPhone = useIsPhone();

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
      if (!form.company.trim()) return;
      setSaving(true);
      try { await onSave(form); onClose(); }
      catch (e) { alert('Save failed: ' + e.message); setSaving(false); }
    };

    // ── View-mode field ──
    const VField = ({ label, value, href, mono }) => (
      <div style={{ marginBottom: 11 }}>
        <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginBottom: 2, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
        {value
          ? href
            ? <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                style={{ fontSize: 13, color: 'var(--accent-text)', textDecoration: 'none', fontFamily: mono ? 'var(--mono)' : 'inherit', wordBreak: 'break-all' }}>
                {value}
              </a>
            : <div style={{ fontSize: 13, color: 'var(--text)', fontFamily: mono ? 'var(--mono)' : 'inherit' }}>{value}</div>
          : <div style={{ fontSize: 12, color: 'var(--text-subtle)', fontStyle: 'italic' }}>Not set</div>
        }
      </div>
    );

    return (
      <div className="modal-wrap">
        {/* Backdrop click does NOT close - use the X button / Cancel to avoid losing data. */}
        <div className="modal" style={{ width: 700, maxWidth: '95vw' }}>

          {/* Header */}
          <div className="modal-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <h2 style={{ margin: 0 }}>
                {isNew ? 'Add supplier contact' : editing ? `Editing - ${contact.company}` : contact.company}
              </h2>
              {!isNew && !editing && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <CatBadge cat={contact.category}/>
                  {contact.sla && <SlaBadge sla={contact.sla}/>}
                </div>
              )}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
              {!isNew && !editing && (
                <button className="btn" onClick={() => setEditing(true)}>
                  <Icon name="edit" size={11}/> Edit
                </button>
              )}
              {!isNew && (
                <button
                  className="btn"
                  style={{ color: '#EF4444', borderColor: '#FECACA' }}
                  onClick={() => { if (window.confirm('Delete this contact?')) onDelete(contact.id); }}
                >
                  <Icon name="trash" size={11}/> Delete
                </button>
              )}
              <button className="btn ghost" onClick={onClose}><Icon name="close" size={11}/></button>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body">
            {editing ? (
              // ─── Edit form ────────────────────────────────────────────
              <>
                <div className="field">
                  <label>Company Name <span style={{ color: '#EF4444' }}>*</span></label>
                  <input autoFocus value={form.company} onChange={e => set('company', e.target.value)} placeholder="e.g. Microsoft, Dell, BT"/>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Category</label>
                    <select value={form.category} onChange={e => set('category', e.target.value)}>
                      {CONTACT_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>SLA / Support Level</label>
                    <select value={form.sla} onChange={e => set('sla', e.target.value)}>
                      <option value="">- Not specified -</option>
                      {SLA_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Contact Name</label>
                    <input value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="Primary contact person"/>
                  </div>
                  <div className="field">
                    <label>Role / Job Title</label>
                    <input value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Account Manager"/>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@supplier.com"/>
                  </div>
                  <div className="field">
                    <label>Phone</label>
                    <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44 20 1234 5678"/>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Support Phone <span style={{ fontWeight: 400, color: 'var(--text-subtle)' }}>(dedicated line)</span></label>
                    <input type="tel" value={form.supportPhone} onChange={e => set('supportPhone', e.target.value)} placeholder="0800 xxx xxxx"/>
                  </div>
                  <div className="field">
                    <label>Support Email</label>
                    <input type="email" value={form.supportEmail} onChange={e => set('supportEmail', e.target.value)} placeholder="support@supplier.com"/>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Contract Expiry</label>
                    <input type="date" value={form.contractExpiry} onChange={e => set('contractExpiry', e.target.value)}/>
                  </div>
                  <div className="field">
                    <label>Account / Ref No.</label>
                    <input value={form.accountNo} onChange={e => set('accountNo', e.target.value)} placeholder="Customer reference"/>
                  </div>
                  <div className="field">
                    <label>Website</label>
                    <input type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://"/>
                  </div>
                </div>
                <div className="field">
                  <label>Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={e => set('notes', e.target.value)}
                    placeholder="Escalation process, billing contacts, known issues, portal URLs, any useful context…"
                    style={{ minHeight: 80 }}
                  />
                </div>
              </>
            ) : (
              // ─── View mode ────────────────────────────────────────────
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: '4px 32px', marginBottom: contact.notes ? 16 : 0 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10, paddingBottom: 5, borderBottom: '1px solid var(--line)' }}>Contact</div>
                    <VField label="Name"    value={contact.contactName}/>
                    <VField label="Role"    value={contact.role}/>
                    <VField label="Email"   value={contact.email}   href={contact.email   ? `mailto:${contact.email}`   : null}/>
                    <VField label="Phone"   value={contact.phone}   href={contact.phone   ? `tel:${contact.phone}`     : null}/>
                    <VField label="Website" value={contact.website} href={contact.website || null}/>
                    <VField label="Account / Ref No." value={contact.accountNo} mono/>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10, paddingBottom: 5, borderBottom: '1px solid var(--line)' }}>Support</div>
                    <VField label="Support Phone" value={contact.supportPhone} href={contact.supportPhone ? `tel:${contact.supportPhone}` : null}/>
                    <VField label="Support Email" value={contact.supportEmail} href={contact.supportEmail ? `mailto:${contact.supportEmail}` : null}/>
                    <div style={{ marginBottom: 11 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginBottom: 4, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>SLA</div>
                      <SlaBadge sla={contact.sla}/>
                    </div>
                    <div style={{ marginBottom: 11 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginBottom: 4, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>Contract Expiry</div>
                      <ExpiryBadge expiry={contact.contractExpiry}/>
                    </div>
                  </div>
                </div>
                {contact.notes && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                    <div style={{
                      fontSize: 13, color: 'var(--text)', lineHeight: 1.65,
                      whiteSpace: 'pre-wrap', background: 'var(--bg-sub)',
                      padding: '10px 12px', borderRadius: 6, border: '1px solid var(--line)',
                    }}>
                      {contact.notes}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer (edit mode only) */}
          {editing && (
            <div className="modal-foot">
              <button className="btn" onClick={() => { isNew ? onClose() : setEditing(false); }}>Cancel</button>
              <button className="btn primary" onClick={save} disabled={!form.company.trim() || saving}>
                {saving ? 'Saving…' : isNew ? 'Add contact' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Main view ────────────────────────────────────────────────────────────

  const ContactsView = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [filterCat, setFilterCat] = useState('all');
    const [sortBy, setSortBy]     = useState({ col: 'company', dir: 'asc' });
    const [selected, setSelected] = useState(null);
    const [addOpen, setAddOpen]   = useState(false);
    const isPhone = useIsPhone();

    const load = useCallback(async () => {
      try {
        const data = await cApi('GET', '/contacts');
        setContacts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('[Contacts] load failed:', e.message);
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAdd = useCallback(async (form) => {
      const created = await cApi('POST', '/contacts', form);
      setContacts(prev => [...prev, created].sort((a, b) => a.company.localeCompare(b.company)));
    }, []);

    const handleSave = useCallback(async (form) => {
      const updated = await cApi('PATCH', `/contacts/${selected.id}`, form);
      setContacts(prev => prev.map(c => c.id === selected.id ? updated : c));
      setSelected(updated);
    }, [selected]);

    const handleDelete = useCallback(async (id) => {
      await cApi('DELETE', `/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      setSelected(null);
    }, []);

    const toggleSort = (col) =>
      setSortBy(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });

    const visible = useMemo(() => {
      const q = search.trim().toLowerCase();
      let list = contacts;
      if (filterCat !== 'all') list = list.filter(c => c.category === filterCat);
      if (q) list = list.filter(c =>
        c.company.toLowerCase().includes(q) ||
        (c.contactName || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.supportEmail || '').toLowerCase().includes(q) ||
        (c.notes || '').toLowerCase().includes(q) ||
        (c.accountNo || '').toLowerCase().includes(q)
      );
      const dir = sortBy.dir === 'asc' ? 1 : -1;
      return list.slice().sort((a, b) => {
        let av = (a[sortBy.col] || '').toLowerCase();
        let bv = (b[sortBy.col] || '').toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    }, [contacts, filterCat, search, sortBy]);

    // Count contracts expiring within 90 days
    const expiringCount = useMemo(() =>
      contacts.filter(c => {
        if (!c.contractExpiry) return false;
        const days = Math.ceil((new Date(c.contractExpiry) - Date.now()) / 86400000);
        return days >= 0 && days <= 90;
      }).length,
    [contacts]);

    const thProps = { sortBy, onSort: toggleSort };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

        {/* ── Toolbar ── */}
        <div className="tk-toolbar2" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Supplier Contacts</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-subtle)' }}>
            {visible.length}{contacts.length !== visible.length ? ` / ${contacts.length}` : ''}
          </span>

          {expiringCount > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
              color: '#D97706', background: '#FFFBEB', border: '1px solid #FCD34D',
            }}>
              ⚠ {expiringCount} contract{expiringCount !== 1 ? 's' : ''} expiring soon
            </span>
          )}

          <span style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 2px' }}/>

          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg-sub)', width: 220 }}>
            <Icon name="search" size={11} style={{ color: 'var(--text-subtle)', flexShrink: 0 }}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search contacts…"
              style={{ border: 0, background: 'transparent', fontSize: 12, width: '100%', outline: 'none', color: 'var(--text)' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ fontSize: 14, color: 'var(--text-subtle)', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}>×</button>
            )}
          </div>

          {/* Category filter */}
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg-elev)', color: 'var(--text)', cursor: 'pointer' }}
          >
            <option value="all">All categories</option>
            {CONTACT_CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {(filterCat !== 'all' || search) && (
            <button className="tk-filter" onClick={() => { setFilterCat('all'); setSearch(''); }}>
              <Icon name="close" size={10}/> Clear
            </button>
          )}

          <div style={{ marginLeft: 'auto' }}>
            <button className="btn primary" onClick={() => setAddOpen(true)}>
              <Icon name="plus" size={11}/> Add contact
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="tk-table" style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="empty"><div>Loading contacts…</div></div>
          ) : visible.length === 0 ? (
            <div className="empty">
              <div>
                <h3>{contacts.length === 0 ? 'No contacts yet' : 'No matches'}</h3>
                <div style={{ marginTop: 4 }}>
                  {contacts.length === 0
                    ? 'Add your first supplier contact using the button above.'
                    : 'Try adjusting the search or category filter.'}
                </div>
              </div>
            </div>
          ) : isPhone ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px 28px' }}>
              {visible.map(c => (
                <div key={c.id} onClick={() => setSelected(c)}
                  style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', minWidth: 0 }}>{c.company}</div>
                    <div style={{ flexShrink: 0 }}><CatBadge cat={c.category}/></div>
                  </div>
                  {(c.contactName || c.role) && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
                      {c.contactName}{c.contactName && c.role ? ' · ' : ''}{c.role}
                    </div>
                  )}
                  {(c.email || c.phone) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8 }}>
                      {c.email && <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, color: 'var(--accent-text)', textDecoration: 'none' }}>{c.email}</a>}
                      {c.phone && <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, color: 'var(--text)', textDecoration: 'none', fontFamily: 'var(--mono)' }}>{c.phone}</a>}
                    </div>
                  )}
                  {(c.sla || c.contractExpiry) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                      {c.sla && <SlaBadge sla={c.sla}/>}
                      {c.contractExpiry && <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Contract expires {c.contractExpiry}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr>
                  <Th label="Company"         col="company"       {...thProps} style={{ minWidth: 160 }}/>
                  <Th label="Category"        col="category"      {...thProps} style={{ minWidth: 140 }}/>
                  <Th label="Contact"         col="contactName"   {...thProps} style={{ minWidth: 140 }}/>
                  <Th label="Email"                               {...thProps} style={{ minWidth: 190 }}/>
                  <Th label="Phone"                               {...thProps} style={{ minWidth: 130 }}/>
                  <Th label="Support"                             {...thProps} style={{ minWidth: 160 }}/>
                  <Th label="SLA"             col="sla"           {...thProps} style={{ minWidth: 110 }}/>
                  <Th label="Contract Expiry" col="contractExpiry"{...thProps} style={{ minWidth: 120 }}/>
                  <Th label="Notes"                               {...thProps} style={{ minWidth: 180 }}/>
                </tr>
              </thead>
              <tbody>
                {visible.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => setSelected(c)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid var(--line)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {/* Company */}
                    <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.website ? (
                        <a
                          href={c.website} target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {c.company}
                        </a>
                      ) : c.company}
                    </td>

                    {/* Category */}
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <CatBadge cat={c.category}/>
                    </td>

                    {/* Contact name + role */}
                    <td style={{ padding: '9px 12px', maxWidth: 160 }}>
                      {c.contactName ? (
                        <>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.contactName}</div>
                          {c.role && <div style={{ fontSize: 11, color: 'var(--text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.role}</div>}
                        </>
                      ) : <Dash/>}
                    </td>

                    {/* Email */}
                    <td style={{ padding: '9px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.email ? (
                        <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 12, color: 'var(--accent-text)', textDecoration: 'none' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >{c.email}</a>
                      ) : <Dash/>}
                    </td>

                    {/* Phone */}
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 12, color: 'var(--text)', textDecoration: 'none', fontFamily: 'var(--mono)' }}
                        >{c.phone}</a>
                      ) : <Dash/>}
                    </td>

                    {/* Support - shows support phone, or support email, or dash */}
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.supportPhone ? (
                        <a href={`tel:${c.supportPhone}`} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 12, color: 'var(--text)', textDecoration: 'none', fontFamily: 'var(--mono)' }}
                        >{c.supportPhone}</a>
                      ) : c.supportEmail ? (
                        <a href={`mailto:${c.supportEmail}`} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 12, color: 'var(--accent-text)', textDecoration: 'none' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >{c.supportEmail}</a>
                      ) : <Dash/>}
                    </td>

                    {/* SLA */}
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <SlaBadge sla={c.sla}/>
                    </td>

                    {/* Contract Expiry */}
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <ExpiryBadge expiry={c.contractExpiry}/>
                    </td>

                    {/* Notes */}
                    <td style={{ padding: '9px 12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-subtle)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {c.notes || <Dash/>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Modals ── */}
        {selected && (
          <ContactModal
            key={selected.id}
            contact={selected}
            isNew={false}
            onClose={() => setSelected(null)}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        )}
        {addOpen && (
          <ContactModal
            key="new"
            contact={null}
            isNew={true}
            onClose={() => setAddOpen(false)}
            onSave={handleAdd}
            onDelete={() => {}}
          />
        )}
      </div>
    );
  };

export { ContactsView };